import { HiPlay, HiXMark } from 'react-icons/hi2';
import type { HistoryItem } from '../../hooks/usePlaybackHistory';

interface ContinueListeningProps {
  items: HistoryItem[];
  fallbackImage: string;
  onPlay: (episodeId: number) => void;
  onRemove: (episodeId: number) => void;
}

export function ContinueListening({
  items,
  fallbackImage,
  onPlay,
  onRemove,
}: ContinueListeningProps) {
  if (items.length === 0) {
    return null;
  }

  const formatTimeLeft = (progress: number, duration: number) => {
    const remaining = Math.floor(duration * (1 - progress));
    const minutes = Math.floor(remaining / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m left`;
    }
    return `${minutes}m left`;
  };

  return (
    <div className="py-3">
      <h3 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wide mb-2 px-3">
        Continue Listening
      </h3>
      <div className="space-y-1 px-2">
        {items.slice(0, 5).map((item) => (
          <div
            key={item.episodeId}
            className="group relative flex items-center gap-2.5 p-2
                       rounded-lg hover:bg-[var(--color-surface-elevated)]/50
                       transition-colors cursor-pointer"
            onClick={() => onPlay(item.episodeId)}
          >
            {/* Thumbnail with progress overlay */}
            <div className="relative flex-shrink-0 w-12 h-12 rounded-md overflow-hidden">
              <img
                src={item.image || fallbackImage}
                alt={item.episodeTitle}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== fallbackImage) {
                    target.src = fallbackImage;
                  }
                }}
              />
              {/* Progress bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/50">
                <div
                  className="h-full bg-[var(--color-primary)]"
                  style={{ width: `${Math.min(item.progress * 100, 100)}%` }}
                />
              </div>
              {/* Play overlay on hover */}
              <div className="absolute inset-0 flex items-center justify-center
                            bg-black/40 opacity-0 group-hover:opacity-100
                            transition-opacity">
                <HiPlay size={20} className="text-white" />
              </div>
            </div>

            {/* Text content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--color-text)] truncate">
                {item.episodeTitle}
              </p>
              <p className="text-xs text-[var(--color-text-muted)] truncate">
                {item.showTitle}
              </p>
              <p className="text-[10px] text-[var(--color-text-muted)]">
                {formatTimeLeft(item.progress, item.duration)}
              </p>
            </div>

            {/* Remove button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(item.episodeId);
              }}
              className="flex-shrink-0 p-1.5 rounded-full
                       text-[var(--color-text-muted)] opacity-0 group-hover:opacity-100
                       hover:text-[var(--color-text)] hover:bg-[var(--color-surface-elevated)]
                       transition-all focus:outline-none focus:opacity-100"
              aria-label={`Remove ${item.episodeTitle} from continue listening`}
            >
              <HiXMark size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
